import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CopyButton,
  Divider,
  Grid,
  Group,
  Loader,
  NumberInput,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDebouncedValue } from '@mantine/hooks';
import { useMutation } from '@tanstack/react-query';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconCheck,
  IconClipboardCheck,
  IconCopy,
  IconDatabase,
  IconFileInvoice,
  IconFlask,
  IconPlus,
  IconReceipt,
  IconRefresh,
  IconSearch,
  IconShieldCheck,
  IconTrash,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { useTranslation } from '@refinedev/core';
import {
  ingestTestRequestSchema,
  type IngestTestRequest,
  type IngestTestResponse,
} from '@billing/validation/ingestTest';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { DateTime } from 'luxon';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  submitIngestTest,
  useListPayors,
  useListProvidersBySite,
  useListSites,
  useListSitesByUser,
  useSearchTariffs,
} from 'api/resource';
import { useCanView, useIsAdmin } from 'hooks';
import { useCurrentUserStore } from 'store/UserStore';

const localNow = (): string =>
  DateTime.now().startOf('minute').toFormat("yyyy-LL-dd'T'HH:mm");

const createInitialValues = (): IngestTestRequest => {
  const serviceStart = localNow();
  const serviceEnd = DateTime.fromISO(serviceStart)
    .plus({ minutes: 30 })
    .toFormat("yyyy-LL-dd'T'HH:mm");

  return {
    mode: 'new_case',
    siteID: '',
    providerID: '',
    payorID: '',
    patient: {
      firstName: 'Synthetic',
      lastName: 'Patient',
      dateOfBirth: '1980-01-01',
      sex: 'X',
      address: '123 Test Street',
      city: 'Winnipeg',
      province: 'MB',
      postalCode: 'R3C 0V8',
      country: 'Canada',
      phone: '',
      email: '',
      maritalStatus: '',
      healthcardNumber: '',
      healthcardRegistration: '',
      healthcardProvince: 'MB',
    },
    claim: {
      serviceStart,
      serviceEnd,
      diagnosis: '',
      locationOfService: '',
      virtualPhysicianRegion: '',
      referringProvider: '00000',
      reportText: 'Synthetic QA ingest test report.',
      providerNotes: '',
      resultStatus: 'F',
      procedureCode: 'QA-TEST',
      procedureDescription: 'Synthetic QA ingest test',
      modality: 'QA',
      facilityName: '',
      facilityRoom: '',
      studyType: 'QA',
      serviceLocationIndicator: '',
      optometristReason: '',
      patientCount: 1,
      confidentialCode: '',
      thirdPartyLiability: '',
      incorporated: '0',
    },
    tariffs: [
      {
        fullCode: '',
        units: 1,
        icd9: '',
        incision: '',
        split: '',
      },
    ],
  };
};

const SectionCard = ({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  children: ReactNode;
}) => (
  <Card withBorder radius="lg" p={{ base: 'md', sm: 'xl' }}>
    <Stack gap="lg">
      <Group gap="sm" align="flex-start" wrap="nowrap">
        <ThemeIcon variant="light" size={42} radius="md">
          {icon}
        </ThemeIcon>
        <Box>
          <Text fw={700} size="lg">
            {title}
          </Text>
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        </Box>
      </Group>
      {children}
    </Stack>
  </Card>
);

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <Group justify="space-between" gap="lg" wrap="nowrap">
    <Text size="sm" c="dimmed">
      {label}
    </Text>
    <Text size="sm" fw={600} ta="right" lineClamp={1}>
      {value}
    </Text>
  </Group>
);

const formatOutcome = (outcome: string): string =>
  outcome
    .replace(/Error$/, '')
    .replaceAll('_', ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2');

const RESULT_STATUS_TRANSLATION_KEYS = {
  P: 'pending',
  F: 'final',
  A: 'amended',
} as const;

export const prepareIngestTestSubmission = (
  values: IngestTestRequest,
  autoGeneratedTestCaseID?: string
): IngestTestRequest => {
  const shouldGenerateNewID =
    values.mode === 'new_case' &&
    Boolean(autoGeneratedTestCaseID) &&
    values.testCaseID === autoGeneratedTestCaseID;

  if (!shouldGenerateNewID) return values;

  const request = { ...values };
  delete request.testCaseID;
  return request;
};

export const IngestTestingPage = () => {
  const { translate } = useTranslation();
  const isAdmin = useIsAdmin();
  const userID = useCurrentUserStore((state) => state.id);
  const { canView: canViewClaims } = useCanView('claims');
  const [tariffSearchInput, setTariffSearchInput] = useState('');
  const [autoGeneratedTestCaseID, setAutoGeneratedTestCaseID] = useState<
    string | undefined
  >();
  const [tariffSearch] = useDebouncedValue(tariffSearchInput, 300);
  const form = useForm<IngestTestRequest>({
    initialValues: createInitialValues(),
    validate: zod4Resolver(ingestTestRequestSchema),
  });
  const allSitesQuery = useListSites({ isAdmin });
  const userSitesQuery = useListSitesByUser({ userID });
  const sitesQuery = isAdmin ? allSitesQuery : userSitesQuery;
  const sites = sitesQuery.data ?? [];
  const providersQuery = useListProvidersBySite({ siteID: form.values.siteID });
  const providers = providersQuery.data ?? [];
  const payorsQuery = useListPayors();
  const payors = payorsQuery.data ?? [];
  const selectedSite = sites.find((site) => site.id === form.values.siteID);
  const selectedProvider = providers.find(
    (provider) => provider.id === form.values.providerID
  );
  const selectedPayor = payors.find(
    (payor) => payor.id === form.values.payorID
  );
  const tariffQuery = useSearchTariffs({
    searchValue: tariffSearch,
    specialtyCode: selectedProvider?.specialtyCode,
    serviceStart: form.values.claim.serviceStart,
    province: selectedPayor?.province ?? 'MB',
  });
  const mutation = useMutation<IngestTestResponse, Error, IngestTestRequest>({
    mutationFn: submitIngestTest,
    onSuccess: (response, submittedRequest) => {
      if (
        submittedRequest.mode === 'new_case' &&
        !submittedRequest.testCaseID
      ) {
        setAutoGeneratedTestCaseID(response.testCaseID);
      }
      form.setFieldValue('testCaseID', response.testCaseID);
    },
  });

  const createTariffRow = (tariffID?: string, fullCode = '') => ({
    tariffID,
    fullCode,
    units: 1,
    icd9: '',
    incision: '' as const,
    split: '' as const,
  });

  const addTariff = (tariffID?: string, fullCode = '') => {
    const tariff = createTariffRow(tariffID, fullCode);
    const hasSingleBlankRow =
      form.values.tariffs.length === 1 &&
      !form.values.tariffs[0].fullCode &&
      !form.values.tariffs[0].tariffID;

    if (hasSingleBlankRow) {
      form.replaceListItem('tariffs', 0, tariff);
    } else {
      form.insertListItem('tariffs', tariff);
    }
    if (tariffID) setTariffSearchInput('');
  };

  const modeOptions = [
    {
      value: 'new_case',
      label: translate('pages:ingestTesting.modes.newCase'),
      description: translate('pages:ingestTesting.modeDescriptions.newCase'),
      icon: <IconPlus size={18} />,
    },
    {
      value: 'update_result',
      label: translate('pages:ingestTesting.modes.updateResult'),
      description: translate(
        'pages:ingestTesting.modeDescriptions.updateResult'
      ),
      icon: <IconRefresh size={18} />,
    },
    {
      value: 'replay_dft',
      label: translate('pages:ingestTesting.modes.replayDft'),
      description: translate('pages:ingestTesting.modeDescriptions.replayDft'),
      icon: <IconDatabase size={18} />,
    },
  ];
  const selectedMode = modeOptions.find(
    (option) => option.value === form.values.mode
  );
  const selectedProviderName = selectedProvider
    ? `${selectedProvider.lastName}, ${selectedProvider.firstName}`
    : translate('pages:ingestTesting.summary.notSelected');
  const result = mutation.data;

  return (
    <Stack
      p={{ base: 'md', lg: 'xl' }}
      gap="xl"
      maw={1440}
      mx="auto"
      data-testid="ingest-testing-page"
    >
      <Group justify="space-between" align="flex-start">
        <Group gap="md" wrap="nowrap" align="flex-start">
          <ThemeIcon size={52} radius="lg" variant="gradient">
            <IconFlask size={28} stroke={1.7} />
          </ThemeIcon>
          <Box>
            <Group gap="sm">
              <Title order={2}>{translate('pages:ingestTesting.title')}</Title>
              <Badge variant="light" color="violet">
                {translate('pages:ingestTesting.nonProduction')}
              </Badge>
            </Group>
            <Text c="dimmed" maw={720} mt={4}>
              {translate('pages:ingestTesting.description')}
            </Text>
          </Box>
        </Group>
      </Group>

      <Alert
        icon={<IconShieldCheck size={20} />}
        color="yellow"
        variant="light"
        radius="md"
        title={translate('pages:ingestTesting.syntheticOnly')}
      >
        {translate('pages:ingestTesting.syntheticWarning')}
      </Alert>

      <form
        onSubmit={form.onSubmit((values) =>
          mutation.mutate(
            prepareIngestTestSubmission(values, autoGeneratedTestCaseID)
          )
        )}
      >
        <Grid gutter="xl">
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <Stack gap="lg">
              <SectionCard
                icon={<IconFlask size={22} />}
                title={translate('pages:ingestTesting.sections.case')}
                description={translate(
                  'pages:ingestTesting.sectionDescriptions.case'
                )}
              >
                <Radio.Group
                  value={form.values.mode}
                  onChange={(mode) =>
                    form.setFieldValue(
                      'mode',
                      mode as IngestTestRequest['mode']
                    )
                  }
                >
                  <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                    {modeOptions.map((option) => (
                      <Radio.Card
                        key={option.value}
                        value={option.value}
                        withBorder
                        radius="md"
                        p="md"
                      >
                        <Stack gap="xs">
                          <Group justify="space-between" wrap="nowrap">
                            <ThemeIcon variant="light" radius="md">
                              {option.icon}
                            </ThemeIcon>
                            <Radio.Indicator />
                          </Group>
                          <Text fw={650} size="sm">
                            {option.label}
                          </Text>
                          <Text size="xs" c="dimmed" lh={1.45}>
                            {option.description}
                          </Text>
                        </Stack>
                      </Radio.Card>
                    ))}
                  </SimpleGrid>
                </Radio.Group>

                <TextInput
                  label={translate('pages:ingestTesting.fields.testCaseID')}
                  description={translate(
                    'pages:ingestTesting.fields.testCaseIDHelp'
                  )}
                  placeholder={translate(
                    'pages:ingestTesting.fields.testCaseIDPlaceholder'
                  )}
                  required={form.values.mode !== 'new_case'}
                  leftSection={<IconClipboardCheck size={17} />}
                  {...form.getInputProps('testCaseID')}
                  onChange={(event) => {
                    const testCaseID = event.currentTarget.value;
                    form.setFieldValue('testCaseID', testCaseID);
                    if (testCaseID !== autoGeneratedTestCaseID) {
                      setAutoGeneratedTestCaseID(undefined);
                    }
                  }}
                />

                <Divider />

                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
                  <Select
                    searchable
                    required
                    label={translate('pages:ingestTesting.fields.site')}
                    placeholder={translate(
                      'pages:ingestTesting.placeholders.selectSite'
                    )}
                    nothingFoundMessage={translate(
                      'pages:ingestTesting.empty.noSites'
                    )}
                    rightSection={
                      sitesQuery.isLoading ? <Loader size="xs" /> : undefined
                    }
                    data={sites.map((site) => ({
                      value: site.id,
                      label: site.name,
                    }))}
                    {...form.getInputProps('siteID')}
                    onChange={(siteID) => {
                      form.setFieldValue('siteID', siteID ?? '');
                      form.setFieldValue('providerID', '');
                    }}
                  />
                  <Select
                    searchable
                    required
                    label={translate('pages:ingestTesting.fields.provider')}
                    placeholder={translate(
                      'pages:ingestTesting.placeholders.selectProvider'
                    )}
                    nothingFoundMessage={translate(
                      'pages:ingestTesting.empty.noProviders'
                    )}
                    rightSection={
                      providersQuery.isLoading ? (
                        <Loader size="xs" />
                      ) : undefined
                    }
                    data={providers.map((provider) => ({
                      value: provider.id,
                      label: `${provider.lastName}, ${provider.firstName}`,
                    }))}
                    disabled={!form.values.siteID}
                    {...form.getInputProps('providerID')}
                  />
                  <Select
                    searchable
                    required
                    label={translate('pages:ingestTesting.fields.payor')}
                    placeholder={translate(
                      'pages:ingestTesting.placeholders.selectPayor'
                    )}
                    nothingFoundMessage={translate(
                      'pages:ingestTesting.empty.noPayors'
                    )}
                    rightSection={
                      payorsQuery.isLoading ? <Loader size="xs" /> : undefined
                    }
                    data={payors.map((payor) => ({
                      value: payor.id,
                      label: payor.name,
                    }))}
                    {...form.getInputProps('payorID')}
                  />
                </SimpleGrid>
              </SectionCard>

              <SectionCard
                icon={<IconUser size={22} />}
                title={translate('pages:ingestTesting.sections.patient')}
                description={translate(
                  'pages:ingestTesting.sectionDescriptions.patient'
                )}
              >
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                  <TextInput
                    required
                    label={translate('pages:ingestTesting.fields.firstName')}
                    {...form.getInputProps('patient.firstName')}
                  />
                  <TextInput
                    required
                    label={translate('pages:ingestTesting.fields.lastName')}
                    {...form.getInputProps('patient.lastName')}
                  />
                  <TextInput
                    required
                    type="date"
                    label={translate('pages:ingestTesting.fields.dateOfBirth')}
                    {...form.getInputProps('patient.dateOfBirth')}
                  />
                  <Select
                    required
                    label={translate('pages:ingestTesting.fields.sex')}
                    data={[
                      {
                        value: 'M',
                        label: translate('pages:ingestTesting.sex.male'),
                      },
                      {
                        value: 'F',
                        label: translate('pages:ingestTesting.sex.female'),
                      },
                      {
                        value: 'X',
                        label: translate('pages:ingestTesting.sex.unspecified'),
                      },
                    ]}
                    {...form.getInputProps('patient.sex')}
                  />
                  <TextInput
                    required
                    label={translate('pages:ingestTesting.fields.address')}
                    {...form.getInputProps('patient.address')}
                  />
                  <TextInput
                    required
                    label={translate('pages:ingestTesting.fields.city')}
                    {...form.getInputProps('patient.city')}
                  />
                  <TextInput
                    required
                    label={translate('pages:ingestTesting.fields.province')}
                    {...form.getInputProps('patient.province')}
                  />
                  <TextInput
                    required
                    label={translate('pages:ingestTesting.fields.postalCode')}
                    {...form.getInputProps('patient.postalCode')}
                  />
                </SimpleGrid>

                <Divider
                  label={translate('pages:ingestTesting.sections.healthcard')}
                  labelPosition="left"
                />

                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
                  <TextInput
                    label={translate(
                      'pages:ingestTesting.fields.healthcardNumber'
                    )}
                    {...form.getInputProps('patient.healthcardNumber')}
                  />
                  <TextInput
                    label={translate(
                      'pages:ingestTesting.fields.healthcardRegistration'
                    )}
                    {...form.getInputProps('patient.healthcardRegistration')}
                  />
                  <TextInput
                    required
                    label={translate(
                      'pages:ingestTesting.fields.healthcardProvince'
                    )}
                    {...form.getInputProps('patient.healthcardProvince')}
                  />
                </SimpleGrid>
              </SectionCard>

              <SectionCard
                icon={<IconFileInvoice size={22} />}
                title={translate('pages:ingestTesting.sections.claim')}
                description={translate(
                  'pages:ingestTesting.sectionDescriptions.claim'
                )}
              >
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                  <TextInput
                    required
                    type="datetime-local"
                    label={translate('pages:ingestTesting.fields.serviceStart')}
                    {...form.getInputProps('claim.serviceStart')}
                  />
                  <TextInput
                    required
                    type="datetime-local"
                    label={translate('pages:ingestTesting.fields.serviceEnd')}
                    {...form.getInputProps('claim.serviceEnd')}
                  />
                  <Select
                    required
                    label={translate('pages:ingestTesting.fields.resultStatus')}
                    data={[
                      {
                        value: 'P',
                        label: translate(
                          'pages:ingestTesting.resultStatuses.pending'
                        ),
                      },
                      {
                        value: 'F',
                        label: translate(
                          'pages:ingestTesting.resultStatuses.final'
                        ),
                      },
                      {
                        value: 'A',
                        label: translate(
                          'pages:ingestTesting.resultStatuses.amended'
                        ),
                      },
                    ]}
                    {...form.getInputProps('claim.resultStatus')}
                  />
                  <TextInput
                    label={translate('pages:ingestTesting.fields.diagnosis')}
                    placeholder={translate(
                      'pages:ingestTesting.fields.diagnosisPlaceholder'
                    )}
                    {...form.getInputProps('claim.diagnosis')}
                  />
                </SimpleGrid>
                <Textarea
                  required
                  autosize
                  minRows={4}
                  maxRows={8}
                  label={translate('pages:ingestTesting.fields.reportText')}
                  {...form.getInputProps('claim.reportText')}
                />

                <Accordion variant="contained" radius="md">
                  <Accordion.Item value="advanced">
                    <Accordion.Control>
                      <Text fw={600} size="sm">
                        {translate('pages:ingestTesting.sections.advanced')}
                      </Text>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                        {[
                          'locationOfService',
                          'virtualPhysicianRegion',
                          'referringProvider',
                          'procedureCode',
                          'procedureDescription',
                          'modality',
                          'facilityName',
                          'facilityRoom',
                          'studyType',
                          'serviceLocationIndicator',
                          'optometristReason',
                          'confidentialCode',
                          'thirdPartyLiability',
                          'incorporated',
                        ].map((field) => (
                          <TextInput
                            key={field}
                            label={translate(
                              `pages:ingestTesting.fields.${field}`
                            )}
                            {...form.getInputProps(`claim.${field}`)}
                          />
                        ))}
                        <NumberInput
                          min={1}
                          label={translate(
                            'pages:ingestTesting.fields.patientCount'
                          )}
                          {...form.getInputProps('claim.patientCount')}
                        />
                      </SimpleGrid>
                      <Textarea
                        mt="lg"
                        autosize
                        minRows={2}
                        label={translate(
                          'pages:ingestTesting.fields.providerNotes'
                        )}
                        {...form.getInputProps('claim.providerNotes')}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </SectionCard>

              <SectionCard
                icon={<IconReceipt size={22} />}
                title={translate('pages:ingestTesting.sections.tariffs')}
                description={translate(
                  'pages:ingestTesting.sectionDescriptions.tariffs'
                )}
              >
                <Group align="flex-end" wrap="wrap">
                  <TextInput
                    flex="1 1 320px"
                    label={translate('pages:ingestTesting.fields.tariffSearch')}
                    description={translate(
                      'pages:ingestTesting.fields.tariffSearchHelp'
                    )}
                    placeholder={translate(
                      'pages:ingestTesting.fields.tariffSearchPlaceholder'
                    )}
                    leftSection={<IconSearch size={17} />}
                    rightSection={
                      tariffQuery.isFetching ? <Loader size="xs" /> : undefined
                    }
                    value={tariffSearchInput}
                    onChange={(event) =>
                      setTariffSearchInput(event.currentTarget.value)
                    }
                  />
                  <Button
                    type="button"
                    variant="default"
                    leftSection={<IconPlus size={16} />}
                    onClick={() => addTariff()}
                  >
                    {translate('pages:ingestTesting.actions.addManualTariff')}
                  </Button>
                </Group>

                {tariffSearch && !tariffQuery.isFetching && (
                  <Card withBorder radius="md" p="xs">
                    {(tariffQuery.data ?? []).length > 0 ? (
                      <Stack gap={4}>
                        {(tariffQuery.data ?? []).slice(0, 8).map((tariff) => {
                          const fullCode = `${tariff.prefix}${tariff.code}`;
                          const isSelected = form.values.tariffs.some(
                            (row) => row.tariffID === tariff.id
                          );
                          return (
                            <UnstyledButton
                              key={tariff.id}
                              disabled={isSelected}
                              p="sm"
                              style={{
                                borderRadius: 'var(--mantine-radius-sm)',
                              }}
                              onClick={() => addTariff(tariff.id, fullCode)}
                            >
                              <Group justify="space-between" wrap="nowrap">
                                <Box style={{ minWidth: 0 }}>
                                  <Group gap="xs">
                                    <Text fw={700} size="sm">
                                      {fullCode}
                                    </Text>
                                    {isSelected && (
                                      <Badge size="xs" variant="light">
                                        {translate(
                                          'pages:ingestTesting.selected'
                                        )}
                                      </Badge>
                                    )}
                                  </Group>
                                  <Text size="xs" c="dimmed" truncate>
                                    {tariff.description}
                                  </Text>
                                </Box>
                                {!isSelected && (
                                  <IconPlus
                                    size={17}
                                    color="var(--mantine-color-blue-6)"
                                  />
                                )}
                              </Group>
                            </UnstyledButton>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed" ta="center" py="md">
                        {translate('pages:ingestTesting.empty.noTariffs')}
                      </Text>
                    )}
                  </Card>
                )}

                <Stack gap="sm">
                  {form.values.tariffs.map((tariff, index) => (
                    <Card
                      withBorder
                      radius="md"
                      p="md"
                      key={`${tariff.tariffID ?? 'manual'}-${index}`}
                    >
                      <Stack gap="md">
                        <Group justify="space-between">
                          <Group gap="xs">
                            <Text fw={650} size="sm">
                              {translate('pages:ingestTesting.tariffRow', {
                                number: index + 1,
                              })}
                            </Text>
                            <Badge
                              size="sm"
                              variant="light"
                              color={tariff.tariffID ? 'blue' : 'gray'}
                            >
                              {tariff.tariffID
                                ? translate('pages:ingestTesting.catalogTariff')
                                : translate('pages:ingestTesting.manualTariff')}
                            </Badge>
                          </Group>
                          <Tooltip
                            label={translate(
                              'pages:ingestTesting.actions.removeTariff'
                            )}
                          >
                            <ActionIcon
                              type="button"
                              color="red"
                              variant="subtle"
                              disabled={form.values.tariffs.length === 1}
                              aria-label={translate(
                                'pages:ingestTesting.actions.removeTariff'
                              )}
                              onClick={() =>
                                form.removeListItem('tariffs', index)
                              }
                            >
                              <IconTrash size={17} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                        <Grid align="flex-end">
                          <Grid.Col span={{ base: 12, sm: 4 }}>
                            <TextInput
                              required
                              label={translate(
                                'pages:ingestTesting.fields.tariffCode'
                              )}
                              readOnly={Boolean(tariff.tariffID)}
                              {...form.getInputProps(
                                `tariffs.${index}.fullCode`
                              )}
                            />
                          </Grid.Col>
                          <Grid.Col span={{ base: 6, sm: 2 }}>
                            <NumberInput
                              required
                              min={1}
                              label={translate(
                                'pages:ingestTesting.fields.units'
                              )}
                              {...form.getInputProps(`tariffs.${index}.units`)}
                            />
                          </Grid.Col>
                          <Grid.Col span={{ base: 6, sm: 2 }}>
                            <TextInput
                              label={translate(
                                'pages:ingestTesting.fields.icd9'
                              )}
                              {...form.getInputProps(`tariffs.${index}.icd9`)}
                            />
                          </Grid.Col>
                          <Grid.Col span={{ base: 6, sm: 2 }}>
                            <Select
                              clearable
                              label={translate(
                                'pages:ingestTesting.fields.incision'
                              )}
                              data={['B', 'D', 'S']}
                              {...form.getInputProps(
                                `tariffs.${index}.incision`
                              )}
                            />
                          </Grid.Col>
                          <Grid.Col span={{ base: 6, sm: 2 }}>
                            <TextInput
                              label={translate(
                                'pages:ingestTesting.fields.split'
                              )}
                              {...form.getInputProps(`tariffs.${index}.split`)}
                            />
                          </Grid.Col>
                        </Grid>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              </SectionCard>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Stack
              gap="md"
              data-testid="ingest-test-summary-panel"
              style={{
                position: 'sticky',
                top: 'calc(var(--app-shell-header-height, 60px) + var(--mantine-spacing-md))',
              }}
            >
              <Card withBorder radius="lg" p="lg">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text fw={700} size="lg">
                      {translate('pages:ingestTesting.summary.title')}
                    </Text>
                    <Badge variant="dot" color="blue">
                      {translate('pages:ingestTesting.summary.ready')}
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    {translate('pages:ingestTesting.summary.description')}
                  </Text>
                  <Divider />
                  <Stack gap="sm">
                    <SummaryRow
                      label={translate('pages:ingestTesting.fields.mode')}
                      value={selectedMode?.label ?? ''}
                    />
                    <SummaryRow
                      label={translate('pages:ingestTesting.fields.site')}
                      value={
                        selectedSite?.name ??
                        translate('pages:ingestTesting.summary.notSelected')
                      }
                    />
                    <SummaryRow
                      label={translate('pages:ingestTesting.fields.provider')}
                      value={selectedProviderName}
                    />
                    <SummaryRow
                      label={translate('pages:ingestTesting.fields.payor')}
                      value={
                        selectedPayor?.name ??
                        translate('pages:ingestTesting.summary.notSelected')
                      }
                    />
                    <SummaryRow
                      label={translate('pages:ingestTesting.sections.tariffs')}
                      value={String(form.values.tariffs.length)}
                    />
                    <SummaryRow
                      label={translate(
                        'pages:ingestTesting.fields.resultStatus'
                      )}
                      value={translate(
                        `pages:ingestTesting.resultStatuses.${
                          RESULT_STATUS_TRANSLATION_KEYS[
                            form.values.claim.resultStatus
                          ]
                        }`
                      )}
                    />
                  </Stack>
                  <Alert
                    color="blue"
                    variant="light"
                    icon={<IconShieldCheck size={18} />}
                  >
                    <Text size="xs">
                      {translate('pages:ingestTesting.summary.privacy')}
                    </Text>
                  </Alert>
                  {mutation.error && (
                    <Alert
                      color="red"
                      title={translate('pages:ingestTesting.failure')}
                      icon={<IconX size={18} />}
                    >
                      {mutation.error.message}
                    </Alert>
                  )}
                  <Button
                    type="submit"
                    size="md"
                    fullWidth
                    loading={mutation.isPending}
                    rightSection={<IconArrowRight size={17} />}
                  >
                    {translate('pages:ingestTesting.actions.submit')}
                  </Button>
                </Stack>
              </Card>

              {result && (
                <Card
                  withBorder
                  radius="lg"
                  p="lg"
                  data-testid="ingest-test-result"
                >
                  <Stack gap="md">
                    <Group justify="space-between" align="flex-start">
                      <Group gap="sm" wrap="nowrap">
                        <ThemeIcon
                          color={result.success ? 'green' : 'red'}
                          variant="light"
                          radius="xl"
                          size="lg"
                        >
                          {result.success ? (
                            <IconCheck size={20} />
                          ) : (
                            <IconX size={20} />
                          )}
                        </ThemeIcon>
                        <Box>
                          <Text fw={700}>
                            {translate('pages:ingestTesting.result')}
                          </Text>
                          <Text
                            size="sm"
                            c={result.success ? 'green' : 'red'}
                            fw={600}
                          >
                            {result.success
                              ? translate('pages:ingestTesting.success')
                              : translate('pages:ingestTesting.failure')}
                          </Text>
                        </Box>
                      </Group>
                    </Group>

                    <Box>
                      <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                        {translate('pages:ingestTesting.fields.testCaseID')}
                      </Text>
                      <Group gap="xs" wrap="nowrap">
                        <Text ff="monospace" fw={600} size="sm" truncate>
                          {result.testCaseID}
                        </Text>
                        <CopyButton value={result.testCaseID}>
                          {({ copied, copy }) => (
                            <Tooltip
                              label={translate(
                                copied
                                  ? 'pages:ingestTesting.actions.copied'
                                  : 'pages:ingestTesting.actions.copyID'
                              )}
                            >
                              <ActionIcon
                                type="button"
                                variant="subtle"
                                color={copied ? 'green' : 'gray'}
                                onClick={copy}
                                aria-label={translate(
                                  'pages:ingestTesting.actions.copyID'
                                )}
                              >
                                {copied ? (
                                  <IconCheck size={16} />
                                ) : (
                                  <IconCopy size={16} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Group>
                    </Box>

                    <Box>
                      <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                        {translate('pages:ingestTesting.outcome')}
                      </Text>
                      <Text size="sm" fw={600} tt="capitalize">
                        {formatOutcome(result.outcome)}
                      </Text>
                    </Box>

                    <Divider />
                    <Text size="sm" fw={650}>
                      {translate('pages:ingestTesting.processingStages')}
                    </Text>
                    <Stack gap="xs">
                      {result.stages.map((stage) => (
                        <Group
                          key={stage.messageType}
                          justify="space-between"
                          wrap="nowrap"
                        >
                          <Group gap="xs" wrap="nowrap">
                            <ThemeIcon
                              size="sm"
                              radius="xl"
                              variant="light"
                              color={stage.success ? 'green' : 'red'}
                            >
                              {stage.success ? (
                                <IconCheck size={13} />
                              ) : (
                                <IconX size={13} />
                              )}
                            </ThemeIcon>
                            <Text size="sm">
                              {translate(
                                `pages:ingestTesting.stages.${stage.messageType.toLowerCase()}`
                              )}
                            </Text>
                          </Group>
                          {stage.error && (
                            <Text size="xs" c="red" ta="right">
                              {formatOutcome(stage.error)}
                            </Text>
                          )}
                        </Group>
                      ))}
                    </Stack>

                    {result.claims.length > 0 ? (
                      <>
                        <Divider />
                        <Text size="sm" fw={650}>
                          {translate('pages:ingestTesting.createdClaims')}
                        </Text>
                        <Stack gap="xs">
                          {result.claims.map((claim) => (
                            <Card withBorder radius="md" p="sm" key={claim.id}>
                              <Group justify="space-between" wrap="nowrap">
                                <Box>
                                  <Badge size="sm" variant="light">
                                    {claim.status}
                                  </Badge>
                                  <Text size="xs" c="dimmed" mt={4}>
                                    {claim.tariffCount}{' '}
                                    {translate(
                                      'pages:ingestTesting.tariffCount'
                                    )}
                                  </Text>
                                </Box>
                                {canViewClaims && (
                                  <Button
                                    component={Link}
                                    to={`/claims/edit/${claim.id}`}
                                    size="compact-sm"
                                    variant="light"
                                  >
                                    {translate(
                                      'pages:ingestTesting.actions.openClaim'
                                    )}
                                  </Button>
                                )}
                              </Group>
                            </Card>
                          ))}
                        </Stack>
                      </>
                    ) : (
                      <Alert
                        color="gray"
                        variant="light"
                        icon={<IconAlertTriangle size={17} />}
                      >
                        <Text size="xs">
                          {translate('pages:ingestTesting.empty.noClaims')}
                        </Text>
                      </Alert>
                    )}
                  </Stack>
                </Card>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
      </form>
    </Stack>
  );
};
